import { GPSSource, PosterProject } from "PosterEditor/model/poster-project";
import { Point, Size, PointCoordinate, Rect } from "../model/poster-types";
import {
  MapLabel,
  StaticLabel,
  Backdrop,
} from "PosterEditor/model/poster-project";
import { drawBackdrops } from "./canvas-drawing/canvas-drawing-backdrops";
import { drawRouteLines } from "./canvas-drawing/canvas-drawing-route-lines";
import { drawPoints } from "./canvas-drawing/canvas-drawing-points";
import { drawMapLabels } from "./canvas-drawing/canvas-drawing-map-labels";
import { drawStaticLabels } from "./canvas-drawing/canvas-drawing-static-labels";
import { drawData } from "./canvas-drawing/canvas-drawing-data";
import { drawDotGrid } from "./canvas-drawing/canvas-drawing-dot-grid";
import { Element } from "../model/poster-types";
import { SnapLine } from "./rect-snapping";
import { EditingPermissions } from "PosterEditor/model/editing-permissions";
export interface MapDrawingConfig {
  map: React.MutableRefObject<any>;
  canvasElement: React.MutableRefObject<any>;
  originalMapSize: Size;
  scale: number;
  project: PosterProject;
  gpsSource: GPSSource | undefined;
  pointerLocation?: Point;
  dotGridSpacing?: number;
  pointerInteruptRadius: number;
  elementToHighlight: MapElement | undefined;
  selectedElement: Element | undefined;
  isRendering: boolean;
  projectEditingPermissions: EditingPermissions;
  isTemplateEditing: boolean;
  imageCache?: Map<string, HTMLImageElement>;
}

export interface DragHandle {
  position: "leading" | "trailing";
}

export interface MapElement {
  type: "point" | "mapLabel" | "staticLabel" | "backdrop";
  //Point has no element, it's just a point.
  pointCoordinate?: PointCoordinate;
  rect: Rect;
  element?: MapLabel | StaticLabel | Backdrop;
  dragHandleUnderCursor?: DragHandle;
}

export const drawMapElements = (
  config: MapDrawingConfig
): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horizontal: SnapLine | undefined;
  };
} => {
  const ctx = config.canvasElement.current.getContext("2d");
  if (!config.map.current || !ctx)
    return {
      elementUnderPointerLocation: undefined,
      snapLines: { vertical: undefined, horizontal: undefined },
    };

  ctx.clearRect(
    0,
    0,
    config.canvasElement.current.width,
    config.canvasElement.current.height
  );

  // DRAW ROUTE LINES
  const normalPointUnderCursor =
    drawRouteLines(config).elementUnderPointerLocation;

  const backdropUnderCursor = drawBackdrops(config).elementUnderPointerLocation;

  // DRAW LABELS
  const mapLabelUnderCursor = drawMapLabels(config).elementUnderPointerLocation;

  // DRAW STYLED POINTS
  const styledPointUnderCursor = drawPoints(config).elementUnderPointerLocation;

  //DRAW STATIC LABELS
  const staticLabelResult = drawStaticLabels(config);
  const staticLabelUnderCursor = staticLabelResult.elementUnderPointerLocation;

  if (
    config.selectedElement?.type === "backdrop" ||
    config.selectedElement?.type === "staticLabel"
  ) {
    drawDotGrid(config);
  }
  return {
    elementUnderPointerLocation:
      staticLabelUnderCursor ||
      mapLabelUnderCursor ||
      styledPointUnderCursor ||
      backdropUnderCursor ||
      normalPointUnderCursor,
    snapLines: {
      vertical: undefined, // staticLabelResult.snapLines.vertical,
      horizontal: undefined, //staticLabelResult.snapLines.horiztonal,
    },
  };
};

// Shared canvas drawing functions.

export const drawSelectedRect = (
  textRect: Rect,
  ctx: CanvasRenderingContext2D
) => {
  drawDottedRect(textRect, ctx);
};

export const drawDottedRect = (
  textRect: Rect,
  ctx: CanvasRenderingContext2D,
  dotSize = 3, // Size of each dot
  gapSize = 1 // Space between dots
) => {
  const { x, y, width, height } = textRect;

  ctx.save();
  ctx.globalAlpha = 1;

  const cornerRadius = 3;
  const path = new Path2D();
  path.roundRect(x, y, width, height, cornerRadius);

  // Draw black shadow/outline for contrast
  ctx.strokeStyle = "rgba(0, 0, 0, 0.7)";
  ctx.lineWidth = 4;
  ctx.setLineDash([dotSize * 2, gapSize * 2]);
  ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.stroke(path);

  // Draw bright white dashed line on top
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.95)";
  ctx.lineWidth = 2;
  ctx.setLineDash([dotSize * 2, gapSize * 2]);
  ctx.stroke(path);

  // Reset
  ctx.setLineDash([]);
  ctx.restore();
};
